import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  HairlineCard,
  IconButton,
  Icons,
  Link,
  Stack,
  StatusChip,
  Tooltip,
  Typography,
  iconSize,
  palette,
} from '@munifocus/ui';
import { decodePolyline, type DayPlan, type DayStop } from '@munifocus/contracts-gov';
import { AppShell } from '../../components/AppShell';
import { PageIntro } from '../../components/PageIntro';
import { googleRouteUrl } from '../../lib/directions';
import { useMutationToast } from '../../lib/mutation-toast';
import {
  useDayPlan,
  useOptimizeDay,
  useSaveDayPlan,
  type DayQuery,
} from '../../lib/operations-api';
import { formatDistance, formatMinutes } from '../../lib/operations-format';
import { OpsMap } from './OpsMap';
import { LoadError, Loading, OpsScopeNotice, useOpsMunicipality } from './shared';
import { frenchFirstOfMonth } from '@munifocus/utils';

/** A booked re-inspection's start, as Atlantic wall-clock time. */
export function formatAppointmentTime(iso: string, language: string): string {
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
      timeZone: 'America/Moncton',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso)),
  );
}

/** Move one id up or down a list, for the reorder buttons. */
export function moveStop(order: string[], id: string, by: -1 | 1): string[] {
  const at = order.indexOf(id);
  const to = at + by;
  if (at === -1 || to < 0 || to >= order.length) return order;
  const next = [...order];
  [next[at], next[to]] = [next[to] as string, next[at] as string];
  return next;
}

/**
 * "My day": the officer's open files in visiting order, on a map, with road
 * legs and arrival times.
 *
 * The order is the optimizer's until the officer moves a stop; then it is
 * theirs, and "Re-optimize" hands it back. The browser's location is asked for
 * only when the officer clicks "Use my location" — never on open — and is sent
 * once, to order the day from where they stand; it is not stored.
 */
export function MyDay() {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const { status } = useOpsMunicipality((m) => m.canReportTime);
  // Any town where the officer reports time will do: the day spans them all.
  const usable = !!status?.municipalities.some((m) => m.canReportTime);
  const [query, setQuery] = useState<DayQuery>({});
  const day = useDayPlan(query, usable);
  const save = useSaveDayPlan();
  const optimize = useOptimizeDay();
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState(false);

  const plan = day.data;
  const remaining = useMemo(
    () => (plan?.stops ?? []).filter((s) => s.visitStatus !== 'completed'),
    [plan],
  );

  const persist = async (order: string[], skipped: string[]) => {
    try {
      await save.mutateAsync({ date: plan?.date, order, skipped });
    } catch (err) {
      toast.error(err, 'gov.ops.day.saveFailed');
    }
  };

  const ids = (plan?.stops ?? []).map((s) => s.fileId);
  const skippedIds = (plan?.skipped ?? []).map((s) => s.fileId);

  const locateMe = () => {
    if (!('geolocation' in navigator)) {
      setLocationError(true);
      return;
    }
    setLocating(true);
    setLocationError(false);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setLocating(false);
        const where = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        try {
          await optimize.mutateAsync({ date: plan?.date, ...where });
          setQuery((q) => ({ ...q, ...where }));
          toast.success('gov.ops.day.optimized');
        } catch (err) {
          toast.error(err, 'gov.ops.day.optimizeFailed');
        }
      },
      () => {
        setLocating(false);
        setLocationError(true);
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    );
  };

  const reoptimize = async () => {
    try {
      await optimize.mutateAsync({ ...query, date: plan?.date });
      toast.success('gov.ops.day.optimized');
    } catch (err) {
      toast.error(err, 'gov.ops.day.optimizeFailed');
    }
  };

  const navigateUrl = googleRouteUrl(remaining);
  const legs = remaining
    .map((s) => (s.legPolyline ? decodePolyline(s.legPolyline) : []))
    .filter((leg) => leg.length > 1);
  const pins = (plan?.stops ?? [])
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.latitude !== null && s.longitude !== null)
    .map(({ s, i }) => ({
      id: s.fileId,
      lat: s.latitude as number,
      lng: s.longitude as number,
      label: String(i + 1),
      title: `${i + 1}. ${s.reference} — ${s.address}`,
      emphasis: s.visitStatus !== 'completed',
    }));

  return (
    <AppShell title={t('gov.ops.day.title')}>
      <PageIntro>{t('gov.ops.day.intro')}</PageIntro>
      {!usable && <OpsScopeNotice reason={status ? 'notEnabled' : 'loading'} />}
      {usable && day.isLoading && <Loading />}
      {usable && day.isError && (
        <LoadError onRetry={() => void day.refetch()}>{t('gov.ops.day.loadError')}</LoadError>
      )}
      {plan && (
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
          >
            {navigateUrl ? (
              <Button
                variant="contained"
                startIcon={<Icons.Navigation size={iconSize.md} aria-hidden />}
                href={navigateUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('gov.ops.day.startNavigation')}
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<Icons.Navigation size={iconSize.md} aria-hidden />}
                disabled
              >
                {t('gov.ops.day.startNavigation')}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Icons.LocateFixed size={iconSize.md} aria-hidden />}
              disabled={locating || optimize.isPending}
              onClick={locateMe}
            >
              {locating ? t('gov.ops.day.locating') : t('gov.ops.day.locateMe')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Icons.RefreshCw size={iconSize.md} aria-hidden />}
              disabled={optimize.isPending}
              onClick={() => void reoptimize()}
            >
              {t('gov.ops.day.reoptimize')}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ ml: { md: 'auto' } }}>
              {t('gov.ops.day.totals', {
                count: remaining.length,
                distance: formatDistance(plan.totalDistanceMeters, i18n.language),
                duration: formatMinutes(Math.round(plan.totalDurationSeconds / 60)),
              })}
            </Typography>
          </Stack>

          {locationError && (
            <Alert severity="warning">{t('gov.ops.day.locationUnavailable')}</Alert>
          )}
          {plan.estimated && remaining.length > 0 && (
            <Alert severity="info">{t('gov.ops.day.estimated')}</Alert>
          )}
          {plan.manual && (
            <Typography variant="caption" color="text.secondary">
              {t('gov.ops.day.manualOrder')}
            </Typography>
          )}
          {plan.startSource === 'none' && remaining.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('gov.ops.day.noStart')}
            </Typography>
          )}

          {pins.length > 0 && <OpsMap pins={pins} legs={legs} start={plan.start} height={340} />}

          {(plan.appointments?.length ?? 0) > 0 && (
            <HairlineCard sx={{ p: 2 }}>
              <Typography variant="overline" component="h2">
                {t('gov.ops.day.appointmentsTitle')}
              </Typography>
              <Stack component="ul" spacing={0.75} sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {plan.appointments?.map((a) => (
                  <Box component="li" key={a.bookingId}>
                    <Typography variant="body2">
                      <strong>{formatAppointmentTime(a.startsAt, i18n.language)}</strong>
                      {' · '}
                      <Link component={RouterLink} to={`/files/${a.fileId}`}>
                        {a.reference}
                      </Link>
                      {' · '}
                      {a.address}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </HairlineCard>
          )}

          <HairlineCard sx={{ p: 0 }}>
            <Box sx={{ px: 2, pt: 1.5 }}>
              <Typography variant="overline" component="h2">
                {t('gov.ops.day.stopsTitle')}
              </Typography>
            </Box>
            {plan.stops.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
                {t('gov.ops.day.empty')}
              </Typography>
            ) : (
              <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {plan.stops.map((s, i) => (
                  <StopRow
                    key={s.fileId}
                    stop={s}
                    index={i}
                    plan={plan}
                    disabled={save.isPending}
                    onMove={(by) => void persist(moveStop(ids, s.fileId, by), skippedIds)}
                    onSkip={() =>
                      void persist(
                        ids.filter((id) => id !== s.fileId),
                        [...skippedIds, s.fileId],
                      )
                    }
                  />
                ))}
              </Box>
            )}
          </HairlineCard>

          {plan.unrouted.length > 0 && (
            <HairlineCard sx={{ p: 2 }}>
              <Typography variant="overline" component="h2">
                {t('gov.ops.day.unroutedTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('gov.ops.day.unroutedHint')}
              </Typography>
              {plan.unrouted.map((s) => (
                <Typography key={s.fileId} variant="body2">
                  <Link component={RouterLink} to={`/files/${s.fileId}`}>
                    {s.reference}
                  </Link>{' '}
                  · {s.address}
                </Typography>
              ))}
            </HairlineCard>
          )}

          {plan.skipped.length > 0 && (
            <HairlineCard sx={{ p: 2 }}>
              <Typography variant="overline" component="h2">
                {t('gov.ops.day.skippedTitle')}
              </Typography>
              {plan.skipped.map((s) => (
                <Stack key={s.fileId} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {s.reference} · {s.address}
                  </Typography>
                  <Button
                    size="small"
                    disabled={save.isPending}
                    onClick={() =>
                      void persist(
                        [...ids, s.fileId],
                        skippedIds.filter((id) => id !== s.fileId),
                      )
                    }
                  >
                    {t('gov.ops.day.restore')}
                  </Button>
                </Stack>
              ))}
            </HairlineCard>
          )}
        </Stack>
      )}
    </AppShell>
  );
}

function StopRow({
  stop,
  index,
  plan,
  disabled,
  onMove,
  onSkip,
}: {
  stop: DayStop;
  index: number;
  plan: DayPlan;
  disabled: boolean;
  onMove: (by: -1 | 1) => void;
  onSkip: () => void;
}) {
  const { t, i18n } = useTranslation();
  const done = stop.visitStatus === 'completed';
  const time = stop.eta
    ? frenchFirstOfMonth(
        new Intl.DateTimeFormat(i18n.language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
          hour: '2-digit',
          minute: '2-digit',
        }).format(new Date(stop.eta)),
      )
    : null;
  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
        px: 2,
        py: 1.25,
        borderTop: `1px solid ${palette.border.line}`,
        opacity: done ? 0.7 : 1,
      }}
    >
      <Typography sx={{ fontWeight: 700, width: 24, textAlign: 'right' }} aria-hidden>
        {index + 1}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Link component={RouterLink} to={`/files/${stop.fileId}`} sx={{ fontWeight: 600 }}>
            {stop.reference}
          </Link>
          {stop.emergency && (
            <StatusChip status="emergency" label={t('gov.ops.dispatch.emergency')} />
          )}
          {stop.visitStatus && (
            <StatusChip
              status={done ? 'success' : 'pending'}
              label={t(`gov.visits.status.${stop.visitStatus}`)}
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {stop.address} · {stop.localGovernmentName}
        </Typography>
        {!done && stop.legDistanceMeters !== null && (
          <Typography variant="caption" color="text.secondary">
            {t('gov.ops.day.leg', {
              distance: formatDistance(stop.legDistanceMeters, i18n.language),
              duration: formatMinutes(Math.round((stop.legDurationSeconds ?? 0) / 60)),
            })}
            {time ? ` · ${t('gov.ops.day.eta', { time })}` : ''}
          </Typography>
        )}
      </Box>
      {!done && (
        <Stack direction="row" spacing={0.25}>
          <Tooltip title={t('gov.ops.day.moveUp')}>
            <span>
              <IconButton
                size="small"
                aria-label={t('gov.ops.day.moveUpStop', { file: stop.reference })}
                disabled={disabled || index === 0}
                onClick={() => onMove(-1)}
              >
                <Icons.ArrowUp size={iconSize.sm} aria-hidden />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('gov.ops.day.moveDown')}>
            <span>
              <IconButton
                size="small"
                aria-label={t('gov.ops.day.moveDownStop', { file: stop.reference })}
                disabled={disabled || index === plan.stops.length - 1}
                onClick={() => onMove(1)}
              >
                <Icons.ArrowDown size={iconSize.sm} aria-hidden />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('gov.ops.day.skip')}>
            <span>
              <IconButton
                size="small"
                aria-label={t('gov.ops.day.skipStop', { file: stop.reference })}
                disabled={disabled}
                onClick={onSkip}
              >
                <Icons.EyeOff size={iconSize.sm} aria-hidden />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
}
